import { ContextBuilder } from './context';
import { formatDateString } from './utils/date';
import { getNestedValue, getNestedValueWithGlob } from './utils/object';
import { minimatch } from 'minimatch';

export class PolicyEngine {
	constructor(policies, context) {
		this._policies = policies;
		this._context = context;
	}

	static init(userData, policies, scopedActions = null) {
		const contextBuilder = new ContextBuilder();
		const engineContext = contextBuilder
			.setEnvironment({
				currentBrowser: 'Chrome',
				currentIP: '192.0.0.1',
				currentPlaftorm: 'Linux',
			})
			.setUser(userData)
			.setScopedActions(scopedActions)
			.addServices('bravas')
			.build();

		const policyEngine = new PolicyEngine(
			(policies || []).map((policy) => ({
				effect: policy.effect,
				authorityPath: policy.authorityPath,
				actions: policy.actions,
				conditions: policy.conditions,
			})),
			engineContext
		);

		return policyEngine;
	}

	formatA(a, b) {
		if (a instanceof Date && typeof b === 'string') {
			return formatDateString(a);
		}
		return a;
	}

	formatB(a, b) {
		return b;
	}

	match(query, context) {
		if (!query) return true;

		const ops = {
			$eq: (a, b) => {
				if (typeof a === 'string' && typeof b === 'string') {
					return minimatch(this.formatA(a, b), this.formatB(a, b));
				}
				return a == b;
			},
			$ne: (a, b) => a !== b,
			$gt: (a, b) => a > b,
			$lt: (a, b) => a < b,
			$gte: (a, b) => a >= b,
			$lte: (a, b) => a <= b,
			$in: (a, b) => b.includes(a),
			$nin: (a, b) => !b.includes(a),
		};

		if ('$and' in query) {
			return Object.entries(query.$and).every(([k, q]) =>
				this.match({ [k]: q }, context)
			);
		}

		if ('$or' in query) {
			return Object.entries(query.$or).some(([k, q]) =>
				this.match({ [k]: q }, context)
			);
		}

		if ('$xor' in query) {
			return Object.entries(query.$xor).reduce((acc, [k, q]) => {
				const matched = this.match({ [k]: q }, context);
				if (acc === null) return matched;
				const result = (acc === true ? 1 : 0) ^ (matched === true ? 1 : 0);
				return result === 1;
			}, null);
		}

		return Object.entries(query).every(([key, value]) => {
			const isKeyOperator = key.startsWith('$');

			if (isKeyOperator && typeof value === 'object' && value !== null) {
				return Object.entries(value).every(([field, fieldValue]) => {
					const contextValue = field.includes('*')
						? getNestedValueWithGlob(context, field)
						: getNestedValue(context, field);

					const fn = ops[key];
					const isConditionMatch = Array.isArray(contextValue)
						? contextValue.some((item) => fn?.(item, fieldValue))
						: fn?.(contextValue, fieldValue);
					return isConditionMatch;
				});
			}

			const contextValue = key.includes('*')
				? getNestedValueWithGlob(context, key)
				: getNestedValue(context, key);

			return contextValue === value;
		});
	}

	can(action) {
		if (Array.isArray(action)) return this.canMultiple(action);
		return this.canSingle(action);
	}

	canSingle(action) {
		if (this._context.user.isSuperAdmin) return true;

		const policies = this._policies.filter((p) => {
			if (!p.actions) return true;
			const matchedActions = p.actions.filter((policyAction) =>
				minimatch(action, policyAction)
			);
			return matchedActions?.length > 0;
		});

		if (!policies || policies.length === 0) return false;

		const context = {
			...this._context,
		};

		const actionInNotScoped = context.notScopedActions.some((notScopedAction) =>
			minimatch(action, notScopedAction)
		);

		if (context.scopedActions !== null) {
			if (!actionInNotScoped) {
				const actionInScope = context.scopedActions.some((scopedAction) =>
					minimatch(action, scopedAction)
				);
				if (!actionInScope) {
					return false;
				}
			}
		}

		const matchedPolicies = policies
			.filter((policy) => this.match(policy.conditions, context))
			.sort((policyA, policyB) => {
				if (
					policyA.authorityPath.startsWith(policyB.authorityPath) &&
					policyA.authorityPath.length > policyB.authorityPath.length
				) {
					return -1;
				}
				if (
					policyB.authorityPath.startsWith(policyA.authorityPath) &&
					policyB.authorityPath.length > policyA.authorityPath.length
				) {
					return 1;
				}
				return 0;
			});

		if (matchedPolicies.length === 0) return false;

		const isAllowed = matchedPolicies[0].effect === 'Allow';

		return isAllowed;
	}

	canMultiple(actions) {
		return actions?.every((action) => this.canSingle(action));
	}
}
